import { ReactNode, useState } from "react"
import { Link, useLocation } from "react-router-dom"
import { useAuth } from "../contexts/use-auth"
import {
  HomeIcon,
  UsersIcon,
  UserGroupIcon,
  FilmIcon,
  VideoCameraIcon,
  MagnifyingGlassIcon,
  Bars3Icon,
  XMarkIcon,
  CircleStackIcon,
  CreditCardIcon,
  GiftIcon,
  BuildingOffice2Icon,
  ArrowUpTrayIcon,
  CpuChipIcon,
  Squares2X2Icon,
  EnvelopeIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  PresentationChartLineIcon,
  BoltIcon,
} from "@heroicons/react/24/outline"
import { Breadcrumbs } from "./Breadcrumbs"

interface LayoutProps {
  children: ReactNode
}

export function Layout({ children }: LayoutProps) {
  const { user, logout } = useAuth()
  const location = useLocation()
  const [sidebarOpen, setSidebarOpen] = useState(false)

  const navigation = [
    { name: "Dashboard", href: "/dashboard", icon: HomeIcon },
    { name: "Users", href: "/users", icon: UsersIcon },
    { name: "Offers", href: "/offers", icon: GiftIcon },
    { name: "Pending Registrations", href: "/pending-registrations", icon: EnvelopeIcon },
    { name: "Teams", href: "/teams", icon: UserGroupIcon },
    { name: "Games", href: "/games", icon: VideoCameraIcon },
    { name: "Clips", href: "/clips", icon: FilmIcon },
    { name: "Diagrams", href: "/diagrams", icon: Squares2X2Icon },
    { name: "Search", href: "/search", icon: MagnifyingGlassIcon },
    { name: "Organizations", href: "/organizations", icon: BuildingOffice2Icon },
    { name: "Comms", href: "/comms", icon: ChatBubbleLeftRightIcon },
  ]

  const resourcesNavigation = [
    { name: "Email", href: "/email", icon: EnvelopeIcon },
    { name: "Observability", href: "/observability", icon: ChartBarIcon },
    { name: "Product Stats", href: "/product-stats", icon: PresentationChartLineIcon },
    { name: "Uploads", href: "/uploads", icon: ArrowUpTrayIcon },
    { name: "Jobs", href: "/jobs", icon: CpuChipIcon },
    { name: "Recent Ops", href: "/recent-ops", icon: BoltIcon },
    { name: "Storage", href: "/resources/storage", icon: CircleStackIcon },
    { name: "Subscription Plans", href: "/subscription-plans", icon: CreditCardIcon },
    { name: "Subscriptions", href: "/subscriptions", icon: CreditCardIcon },
    { name: "Affiliates", href: "/affiliates", icon: GiftIcon },
  ]

  // Exact match, except section roots (e.g. /email) also light up on their
  // sub-routes (/email/stats, /email/log, …).
  const isActive = (href: string) =>
    location.pathname === href || location.pathname.startsWith(`${href}/`)

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-50 lg:hidden ${sidebarOpen ? "" : "hidden"}`}>
        <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
        <div className="fixed inset-y-0 left-0 flex w-64 flex-col bg-surface">
          <div className="flex h-16 items-center justify-between px-6">
            <h1 className="text-xl font-bold text-primary">Playside Admin</h1>
            <button
              onClick={() => setSidebarOpen(false)}
              className="text-secondary hover:text-primary"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>
          <nav className="flex-1 space-y-1 px-2 py-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`${
                  isActive(item.href)
                    ? "bg-primary/10 text-primary"
                    : "text-secondary hover:bg-surface/50 hover:text-primary"
                } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors`}
              >
                <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                {item.name}
              </Link>
            ))}

            <div className="pt-4">
              <p className="px-3 text-xs font-semibold text-secondary uppercase tracking-wider">
                Resources
              </p>
              <div className="mt-2 space-y-1">
                {resourcesNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`${
                      isActive(item.href)
                        ? "bg-primary/10 text-primary"
                        : "text-secondary hover:bg-surface/50 hover:text-primary"
                    } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors`}
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-grow flex-col overflow-y-auto border-r border-border bg-surface">
          <div className="flex h-16 flex-shrink-0 items-center px-6">
            <h1 className="text-xl font-bold text-primary">Playside Admin</h1>
          </div>
          <nav className="mt-5 flex-1 space-y-1 px-2">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`${
                  isActive(item.href)
                    ? "bg-primary/10 text-primary"
                    : "text-secondary hover:bg-surface/50 hover:text-primary"
                } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors`}
              >
                <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                {item.name}
              </Link>
            ))}

            <div className="pt-4">
              <p className="px-3 text-xs font-semibold text-secondary uppercase tracking-wider">
                Resources
              </p>
              <div className="mt-2 space-y-1">
                {resourcesNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`${
                      isActive(item.href)
                        ? "bg-primary/10 text-primary"
                        : "text-secondary hover:bg-surface/50 hover:text-primary"
                    } group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors`}
                  >
                    <item.icon className="mr-3 h-5 w-5 flex-shrink-0" />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
          <div className="flex flex-shrink-0 border-t border-border p-4">
            <div className="flex items-center">
              <div>
                <p className="text-sm font-medium">{user?.name}</p>
                <p className="text-xs text-secondary">{user?.email}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-10 flex h-16 flex-shrink-0 bg-background border-b border-border">
          <button
            onClick={() => setSidebarOpen(true)}
            className="px-4 text-secondary hover:text-primary lg:hidden"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>
          <div className="flex flex-1 justify-between px-4 lg:px-8">
            <div className="flex flex-1 items-center">
              <Breadcrumbs />
            </div>
            <div className="ml-4 flex items-center gap-4">
              <button
                onClick={() => logout()}
                className="text-sm text-secondary hover:text-primary transition-colors"
              >
                Sign out
              </button>
            </div>
          </div>
        </div>

        {/* Page content */}
        <main className="flex-1">
          <div className="py-6">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">{children}</div>
          </div>
        </main>
      </div>
    </div>
  )
}
